import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import ProjectPositionDTO from "../dto/ProjectPositionDTO";
import { EnsolversOpsUser } from "../models/EnsolversOpsUser";
import PagedResult from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import OverStaffReport from "../models/OverStaffReport/OverStaffReport";
import HttpClientV2 from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/HttpClientV2";
import ApiResponseV2 from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/ApiResponseV2";
import GenericCrudServiceV2 from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/GenericCrudServiceV2";
import Dashboard from "../models/Dashboard";
import AvailableStaff from "../models/AvailableStaff";

export class UserService extends GenericCrudServiceV2<EnsolversOpsUser> {
  readonly path: string;

  constructor(protected basePath: string) {
    super(basePath);
    this.path = basePath;
  }

  findUserNames(): Promise<ApiResponseV2<AutoCompleteUIDTO[]>> {
    return HttpClientV2.get(`${this.basePath}/user-names`);
  }

  findManagerUserNames(): Promise<ApiResponseV2<AutoCompleteUIDTO[]>> {
    return HttpClientV2.get(`${this.basePath}/manager-user-names`);
  }

  changeUserStatus(externalId: string, enable: boolean): Promise<ApiResponseV2<EnsolversOpsUser>> {
    return HttpClientV2.put(`${this.basePath}/${externalId}/change-status?enable=${enable}`, {});
  }

  findByCoreUserId(externalId: string): Promise<ApiResponseV2<EnsolversOpsUser>> {
    return HttpClientV2.get(`${this.basePath}/user-core/${externalId}`);
  }

  getProjectPositions(externalId: string): Promise<ApiResponseV2<ProjectPositionDTO[]>> {
    return HttpClientV2.get(`${this.basePath}/${externalId}/project-positions`);
  }

  findByUserName(page: number, pageSize: number, userName: string): Promise<ApiResponseV2<PagedResult<EnsolversOpsUser>>> {
    let query = `${this.basePath}/filtered?page=${page}&pageSize=${pageSize}&userName=${userName}`;
    return HttpClientV2.get(query);
  }

  findByRole(page: number, pageSize: number, role: string): Promise<ApiResponseV2<PagedResult<EnsolversOpsUser>>> {
    let query = `${this.basePath}/user-role?page=${page}&pageSize=${pageSize}&role=${role}`;
    return HttpClientV2.get(query);
  }

  findByRoleAndUserName(
    page: number,
    pageSize: number,
    userName: string,
    role: string,
    statusFilter: string,
  ): Promise<ApiResponseV2<PagedResult<EnsolversOpsUser>>> {
    let query = `${this.basePath}/filtered?page=${page}&pageSize=${pageSize}&userName=${userName}&role=${role}&statusFilter=${statusFilter}`;
    return HttpClientV2.get(query);
  }

  getOverStaffReport(year: number, month: number, filter: string, role: string): Promise<ApiResponseV2<OverStaffReport[]>> {
    return HttpClientV2.get(`${this.basePath}/over-staff-report?year=${year}&month=${month}&filter=${filter}&role=${role}`);
  }

  setRole(email: string, role: string, state: boolean): Promise<ApiResponseV2<EnsolversOpsUser>> {
    let query = `${this.basePath}/admin/update-role?email=${email}&role=${role}&state=${state}`;
    return HttpClientV2.post(query, true);
  }

  getMissingInformation(externalId: string): Promise<ApiResponseV2<String[]>> {
    return HttpClientV2.get(`${this.basePath}/get-information?externalId=${externalId}`);
  }

  getDashboard(): Promise<ApiResponseV2<Dashboard>> {
    return HttpClientV2.get(`${this.basePath}/get-dashboard`);
  }

  getAvailableStaff(year: number, month: number): Promise<ApiResponseV2<AvailableStaff[]>> {
    return HttpClientV2.get(`${this.basePath}/get-available-staff?year=${year}&month=${month}`);
  }

  /**
   * @description Regenerates the Drata identifier of the user who called this method, the backend knows the user by the auth
   * @returns The drataIdentifier Promise
   */
  regenerateDrataIdentifier(): Promise<ApiResponseV2<string>> {
    return HttpClientV2.put(`${this.basePath}/self/regenerate-drata-identifier`, {});
  }

  /**
   * @remarks ADMIN PRIVILEGES REQUIRED
   * @description Regenerates the Drata identifier of the user whose externalId matches
   * @param externalId the user identifier, check whether it is from core or from ensolvers_ops
   * @returns The drataIdentifier Promise
   */
  regenerateDrataIdentifierByAdmin(externalId: string): Promise<ApiResponseV2<string>> {
    return HttpClientV2.put(`${this.basePath}/regenerate-drata-identifier?externalId=${externalId}`, {});
  }
}

export default new UserService("/ensolvers-users");
